/**
 * LvcContestBanner component:
 *
 * This component displays a contest banner based on the provided configuration data.
 * It listens to the 'lvc:user_state_changed' event to update its state when the user's state changes.
 *
 * The component has the following logic:
 * - It parses the configuration data from the 'banner-config' attribute on initialization.
 * - It checks the current language and mobile breakpoint to determine which image to display.
 * - It listens to the window resize event to re-render the banner when the window size changes.
 * - It has a showElement method to display the banner.
 * - It has a handleStateChange method to update the banner when the user's state changes.
 *
 * The component expects the following configuration data:
 * - publishAt: string
 * - unpublishAt: string
 * - displayOnlyForUserType: 'all' | 'authenticated' | 'not-authenticated'
 * - imageMobile: { [key: string]: string[] }
 * - imageDesktop: { [key: string]: string[] }
 * - imageAltText: { [key: string]: string[] }
 * - url: { [key: string]: string[] }
 * - urlOpenInNewTab: boolean
 * - gtmData: any
 */

import { EventListeners, MinzeElement } from 'minze'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'

interface BannerData {
  publishAt: string
  unpublishAt: string
  displayOnlyForUserType: 'all' | 'authenticated' | 'not-authenticated'
  imageMobile: {
    [key: string]: string[]
  }
  imageDesktop: {
    [key: string]: string[]
  }
  imageAltText: {
    [key: string]: string[]
  }
  url: {
    [key: string]: string[]
  }
  urlOpenInNewTab: boolean
  gtmData: any
}

export class LvcContestBanner extends MinzeElement {
  private bannerData: BannerData | null = null
  private currentLang: string = Locale.language
  private mobileBreakpoint: number = 730
  private isLoggedIn: boolean = false

  constructor() {
    super()
  }

  onStart = () => {
    this.parseConfig()
  }

  showElement = (value: boolean) => {
    if (value) {
      this.style.display = 'block'
    } else {
      this.style.display = 'none'
    }
  }

  // Analyze the configuration to extract only the data
  parseConfig = () => {
    try {
      const configAttr = this.getAttribute('banner-config')
      if (configAttr) {
        const decodedConfig = decodeURIComponent(configAttr)
        const fullConfig = JSON.parse(decodedConfig)

        // Extract only the "data" part
        if (fullConfig && fullConfig.data) {
          this.bannerData = fullConfig.data
        }
      }
    } catch (error) {
      console.error('Error while parsing the banner configuration:', error)
    }
  }

  handleStateChange = () => {
    this.isLoggedIn = MainModule.getInstance().getAuth().isLoggedIn()

    this.showElement(this.shouldDisplayBanner())

    this.rerender()
  }

  // Check if the banner should be displayed based on user type
  shouldDisplayBanner = () => {
    if (!this.bannerData) return false

    const { displayOnlyForUserType } = this.bannerData

    // Check if the banner should be displayed based on user type
    switch (displayOnlyForUserType) {
      case 'authenticated':
        return this.isLoggedIn
      case 'not-authenticated':
        return !this.isLoggedIn
      case 'all':
      default:
        return true
    }
  }

  // HTML template with responsive images using srcset
  html = () => {
    if (!this.bannerData) {
      return ``
    }

    const mobileImageUrl = this.bannerData.imageMobile[this.currentLang][0]
    const desktopImageUrl = this.bannerData.imageDesktop[this.currentLang][0]
    const altText = this.bannerData.imageAltText[this.currentLang]
    const url = this.bannerData.url[this.currentLang]
    const target = this.bannerData.urlOpenInNewTab ? '_blank' : '_self'

    return `
      <div class="contest-banner">
        <a href="${url}" target="${target}" class="contest-banner__link">
          <picture>
            <source media="(max-width: ${this.mobileBreakpoint}px)" srcset="${mobileImageUrl}">
            <img class="contest-banner__image" src="${desktopImageUrl}" alt="${altText}">
          </picture>
        </a>
      </div>
    `
  }

  css = () => {
    return `
      :host {
        display: none;
        width: 100%;
      }
      
      .contest-banner {
        width: 100%;
        padding: 4rem 0;
      }
      
      .contest-banner__image {
        width: 100%;
        height: auto;
        display: block;
      }
    `
  }

  eventListeners: EventListeners = [[window, 'lvc:user_state_changed', this.handleStateChange]]
}
